// @ts-strict-ignore
import { inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { filter, map, Observable } from 'rxjs';
import { AppRoute } from '../models/app.routes.model';
import { SessionService } from '../services/session.service';
import { UserService } from '../services/user.service';

@Injectable()
export class NonAdminGuard {
  private isCreditBank = false;
  private isMember = false;
  private MemberAuditAccess = false;
  private isAdmin = false;
  protected isTrader = false;
  private isBlotterTrader = false;
  private isFixingOrderTrader = false;

  private readonly userService = inject(UserService);

  constructor(
    private router: Router,
    private sessionService: SessionService,
  ) {}

  public canActivate: CanActivateFn = (
    next: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
  ): Observable<boolean> | Promise<boolean> | boolean => {
    return this.userService.user$.pipe(
      filter((user) => user != null),
      map(() => {
        this.initializeUserRoles();

        const token = this.sessionService.getToken();
        if (token && this.isAdmin) {
          return true;
        } else if (token && this.isCreditBank) {
          return this.handleCreditBankRoutes(next);
        } else if (token && this.isMember) {
          return this.handleMemberRoutes(next);
        } else {
          this.sessionService.logout();
          return false;
        }
      }),
    );
  };

  private initializeUserRoles() {
    this.isCreditBank = this.sessionService.isCreditBank();
    this.isMember = this.sessionService.isMember();
    this.isTrader = this.sessionService.isTrader();
    this.isBlotterTrader = this.sessionService.isBlotterTrader();
    this.isFixingOrderTrader = this.sessionService.isFixingOrderTrader();
    this.MemberAuditAccess =
      this.isMember && this.sessionService.canAccessAudit();
    this.isAdmin = this.sessionService.isAdmin();
  }

  private handleCreditBankRoutes(next: ActivatedRouteSnapshot): boolean {
    const creditBankRoutes = ['reports', 'rates', 'settings', 'credit-limits'];
    const creditBlotterTraderRoutes = ['pricer', 'reports', 'rates'];

    const routes = this.isBlotterTrader
      ? creditBlotterTraderRoutes
      : creditBankRoutes;

    if (!routes.includes(next.routeConfig.path)) {
      const path = this.isBlotterTrader ? 'pricer' : 'reports';
      this.router.navigate([path], { queryParams: next.queryParams });
      return false;
    }
    return true;
  }

  private handleMemberRoutes(next: ActivatedRouteSnapshot): boolean {
    const memberRoutes = [
      'pools',
      'reports',
      'rates',
      'credit',
      ...this.addOptionalRoutes(),
    ];

    const memberAuditAccessRoutes = [...memberRoutes, 'settings'];
    const memberTraderBlotter = [...memberRoutes, AppRoute.Blotter];
    const isFixingOrderTraderRoutes = [AppRoute.Blotter, 'reports', 'rates'];

    let routes: string[];

    if (this.isBlotterTrader) {
      routes = memberTraderBlotter;
    } else if (this.isFixingOrderTrader) {
      routes = isFixingOrderTraderRoutes;
    } else if (this.MemberAuditAccess) {
      routes = memberAuditAccessRoutes;
    } else {
      routes = memberRoutes;
    }

    if (!routes.includes(next.routeConfig.path)) {
      const path =
        this.isBlotterTrader || this.isFixingOrderTrader
          ? AppRoute.Blotter
          : 'pools';
      this.router.navigate([path], { queryParams: next.queryParams });
      return false;
    }
    return true;
  }

  private addOptionalRoutes(): string[] {
    const optionalRoutes = [];

    if (
      this.userService.user$.value?.rolePermissions?.includes('BLOTTER_VIEW')
    ) {
      optionalRoutes.push(AppRoute.Blotter);
    }

    return optionalRoutes;
  }
}
